import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { ShieldCheck, Lock } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22", amber: "#B7791F", green: "#2F7D5A" };
const STATUS_LABEL = { received: "Received", verifying_identity: "Verifying identity", in_review: "In review", completed: "Completed", denied: "Denied" };
const STATUS_TONE = { received: C.navy, verifying_identity: C.amber, in_review: C.amber, completed: C.green, denied: C.red };
const TYPE_LABEL = { data_export: "Data export", data_deletion: "Data deletion", correction: "Correction", opt_out: "Opt-out" };

export default function PrivacyRequests() {
  const [data, setData] = useState({ items: [], counts: {}, statuses: [], types: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/security/privacy-requests", { params });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [filter]);

  const update = async (id, payload) => {
    try {
      const { data: fresh } = await api.put(`/security/privacy-requests/${id}`, payload);
      setSel(fresh);
      setNote("");
      toast.success("Request updated");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="privacy-requests-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-1" style={{ color: C.charcoal }}>
        <ShieldCheck size={22} style={{ color: C.navy }} /> Privacy Requests
      </h1>
      <p className="text-[13px] mb-3" style={{ color: C.slate }}>
        Data export, correction, deletion, and opt-out requests. Verify identity before acting.
        <span className="inline-flex items-center gap-1 ml-1" style={{ color: C.red }}><Lock size={12} /> Deletion requests are handled manually — records are never auto-deleted.</span>
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {["all", ...(data.statuses || [])].map((s) => (
          <button key={s} data-testid={`pr-filter-${s}`} onClick={() => setFilter(s)}
            className="text-[11.5px] px-2.5 py-1 rounded-full border"
            style={{ borderColor: filter === s ? C.navy : C.line, background: filter === s ? "rgba(28,67,88,0.08)" : "#fff", color: filter === s ? C.navy : C.slate }}>
            {s === "all" ? "All" : STATUS_LABEL[s]}{data.counts?.[s] ? ` · ${data.counts[s]}` : ""}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-5 gap-4">
        <div className="md:col-span-3">
          <Card className="border-[#E6EDF2] overflow-hidden">
            {loading ? (
              <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : (data.items || []).length === 0 ? (
              <div className="p-8 text-center text-[13px]" style={{ color: C.slate }} data-testid="pr-empty">No privacy requests.</div>
            ) : (
              <div className="divide-y" style={{ borderColor: C.line }}>
                {data.items.map((r) => (
                  <button key={r.id} data-testid="pr-row" onClick={() => setSel(r)}
                    className="w-full text-left px-4 py-3 hover:bg-[#F7FAFC] transition-colors"
                    style={{ background: sel?.id === r.id ? "#F1F6F9" : "transparent" }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13.5px] font-medium" style={{ color: C.charcoal }}>{r.requester_name}</span>
                      <span className="text-[10.5px] px-1.5 py-0.5 rounded-full capitalize"
                        style={{ background: `${STATUS_TONE[r.status]}1A`, color: STATUS_TONE[r.status] }}>{STATUS_LABEL[r.status]}</span>
                    </div>
                    <div className="text-[11.5px] mt-0.5" style={{ color: C.slate }}>{TYPE_LABEL[r.request_type]} · {r.email} · {formatDateTime(r.created_at)}</div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="md:col-span-2">
          {sel ? (
            <Card className="p-4 border-[#E6EDF2]" data-testid="pr-detail">
              <div className="text-[14px] font-semibold mb-1" style={{ color: C.charcoal }}>{sel.requester_name}</div>
              <div className="text-[12px] mb-3" style={{ color: C.slate }}>{sel.email}{sel.relationship ? ` · ${sel.relationship}` : ""}</div>
              <div className="text-[12.5px] mb-1"><span style={{ color: C.slate }}>Type:</span> {TYPE_LABEL[sel.request_type]}</div>
              {sel.details && <div className="text-[12.5px] mb-3 rounded-lg bg-[#F7FAFC] border p-2" style={{ borderColor: C.line, color: C.charcoal }}>{sel.details}</div>}

              <label className="text-[11.5px]" style={{ color: C.slate }}>Status</label>
              <Select value={sel.status} onValueChange={(v) => update(sel.id, { status: v })}>
                <SelectTrigger className="mt-1 mb-3 h-8" data-testid="pr-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(data.statuses || []).map((s) => <SelectItem key={s} value={s} data-testid={`pr-status-${s}`}>{STATUS_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>

              <label className="text-[11.5px]" style={{ color: C.slate }}>Internal notes</label>
              <div className="space-y-1.5 my-1.5 max-h-40 overflow-y-auto">
                {(sel.notes || []).map((n) => (
                  <div key={n.id} className="text-[11.5px] rounded-md bg-[#F7FAFC] px-2 py-1" style={{ color: C.charcoal }}>
                    <span style={{ color: C.slate }}>{n.by_name}:</span> {n.text}
                  </div>
                ))}
                {(sel.notes || []).length === 0 && <div className="text-[11.5px]" style={{ color: C.slate }}>No notes yet.</div>}
              </div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" className="text-[12.5px]" data-testid="pr-note-input" />
              <Button size="sm" className="mt-2 w-full bg-[#1C4358]" disabled={!note.trim()} onClick={() => update(sel.id, { note })} data-testid="pr-note-add">Add note</Button>
            </Card>
          ) : (
            <Card className="p-8 border-[#E6EDF2] text-center text-[13px]" style={{ color: C.slate }}>Select a request to manage it.</Card>
          )}
        </div>
      </div>
    </div>
  );
}
